<template>
  <div>
    <div
      v-if="loading"
      class="flex items-center justify-center text-center pb-32 pt-2"
    >
      <spinner size="88" :line-size="12" :line-fg-color="$brandColor"></spinner>
    </div>

    <div v-else>
      <!-- Modulo sin desplegar: se muestra el formulario igual y solo se bloquea guardar. -->
      <div
        v-if="!available"
        class="config-flex__notice"
      >
        La configuración de Envíos Flex todavía no está disponible en este ambiente
        ({{ unavailableReason }}). La pantalla se muestra con los valores por defecto y
        no se puede guardar.
      </div>

      <div class="branches-wrapper mb-4">
        <p class="item-title">Dirección de retiro</p>
        <p class="item-subtitle mb-4">Desde acá Cruz del Sur retira los envíos Flex.</p>

        <div
          v-for="row in ORIGIN_ROWS"
          :key="row.map((f) => f.key).join('-')"
          class="config-flex__address-row"
        >
          <div
            v-for="field in row"
            :key="field.key"
            :style="{ flex: field.grow }"
            class="config-flex__address-field"
          >
            <!-- El label va arriba y no en el placeholder, que desaparece al escribir. -->
            <label :for="`origin-${field.key}`" class="config-flex__field-label">
              {{ field.label }}<span
                v-if="isRequired(field.key)"
                class="config-flex__required"
                aria-hidden="true"
              >*</span>
            </label>
            <select
              v-if="field.options"
              :id="`origin-${field.key}`"
              class="input-branch config-flex__select w-full"
              :class="{ 'config-flex__input--invalid': errors[originErrorKey(field.key)] }"
              :value="config.origin_address[field.key] || ''"
              @change="setOriginField(field.key, $event.target.value)"
            >
              <option value="" disabled>Elegí una provincia</option>
              <!-- Si el valor guardado no esta en la lista se agrega como opcion, para no perderlo en silencio al guardar. -->
              <option
                v-if="originValueOutsideOptions(field)"
                :value="config.origin_address[field.key]"
              >
                {{ config.origin_address[field.key] }}
              </option>
              <option
                v-for="opcion in field.options"
                :key="opcion.value"
                :value="opcion.value"
              >
                {{ opcion.label }}
              </option>
            </select>
            <input
              v-else
              :id="`origin-${field.key}`"
              type="text"
              class="input-branch w-full"
              :class="{ 'config-flex__input--invalid': errors[originErrorKey(field.key)] }"
              :maxlength="field.max"
              :value="config.origin_address[field.key] || ''"
              @input="setOriginField(field.key, $event.target.value)"
            />
            <p v-if="errors[originErrorKey(field.key)]" class="config-flex__error">
              {{ errors[originErrorKey(field.key)] }}
            </p>
          </div>
        </div>

        <!-- Aviso antes de apretar Guardar; el boton esta al final y la direccion arriba. -->
        <p v-if="originIncomplete" class="config-flex__error mt-2">
          Completá calle, altura, CP, localidad y provincia: sin la dirección de retiro no se
          puede crear ningún envío Flex.
        </p>
      </div>

      <div class="branches-wrapper mb-4">
        <div class="flex justify-between items-center">
          <div>
            <p class="item-title">Creación de envíos automáticos</p>
            <p class="item-subtitle">
              El envío automático se creará en cuanto el pedido esté en Cruz del Sur,
              dentro de los horarios de operación.
            </p>
          </div>
          <label class="switch ml-4">
            <input
              type="checkbox"
              v-model="config.auto_shipment_enabled"
            />
            <span class="slider round"></span>
          </label>
        </div>
      </div>

      <div class="branches-wrapper">
        <p class="item-title">Horarios de operación</p>
        <p class="item-subtitle mb-4">
          Fuera de estas franjas las órdenes quedan pendientes de despacho manual.
        </p>

        <div
          v-for="(schedule, index) in config.schedules"
          :key="schedule.profile"
          class="config-flex__schedule"
        >
          <div class="flex items-center config-flex__schedule-label">
            <vs-checkbox
              :value="schedule.enabled"
              @input="toggleSchedule(index, $event)"
            >
              {{ profileLabels[schedule.profile] || schedule.profile }}
            </vs-checkbox>
          </div>

          <div class="config-flex__time-range">
            <input
              type="time"
              class="input-branch config-flex__time"
              :disabled="!schedule.enabled"
              :value="schedule.start_time || ''"
              @input="setTime(index, 'start_time', $event.target.value)"
            />
            <span>—</span>
            <input
              type="time"
              class="input-branch config-flex__time"
              :disabled="!schedule.enabled"
              :value="schedule.end_time || ''"
              @input="setTime(index, 'end_time', $event.target.value)"
            />
          </div>

          <p v-if="errors[schedule.profile]" class="config-flex__error">
            {{ errors[schedule.profile] }}
          </p>
        </div>

        <div class="flex justify-end mt-4">
          <vs-button
            :disabled="!available || saving"
            @click="save"
          >
            {{ saving ? 'Guardando...' : 'Guardar cambios' }}
          </vs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from "vue-simple-spinner";

import { getFlexConfig, updateFlexConfig } from "../../services/meliFlex.service";
import { showError, showSuccess } from "../../services/alert.service";
import { errorMessage } from "../../http";

// Direccion de retiro. Reemplaza a la sucursal de CDS (§5.3). `number` es texto: hay "S/N" y "1234 bis".
// `value` es EXACTAMENTE lo que espera CDS; `label` lo que ve el comerciante. Tres difieren: Capital Federal, San Luís y Santa Fé (las tildes son de CDS). NO corregir los `value`.
const ARGENTINA_PROVINCES = [
  { value: "Buenos Aires", label: "Buenos Aires" },
  { value: "Catamarca", label: "Catamarca" },
  { value: "Chaco", label: "Chaco" },
  { value: "Chubut", label: "Chubut" },
  { value: "Capital Federal", label: "Ciudad Autónoma de Buenos Aires" },
  { value: "Córdoba", label: "Córdoba" },
  { value: "Corrientes", label: "Corrientes" },
  { value: "Entre Ríos", label: "Entre Ríos" },
  { value: "Formosa", label: "Formosa" },
  { value: "Jujuy", label: "Jujuy" },
  { value: "La Pampa", label: "La Pampa" },
  { value: "La Rioja", label: "La Rioja" },
  { value: "Mendoza", label: "Mendoza" },
  { value: "Misiones", label: "Misiones" },
  { value: "Neuquén", label: "Neuquén" },
  { value: "Río Negro", label: "Río Negro" },
  { value: "Salta", label: "Salta" },
  { value: "San Juan", label: "San Juan" },
  { value: "San Luís", label: "San Luis" },
  { value: "Santa Cruz", label: "Santa Cruz" },
  { value: "Santa Fé", label: "Santa Fe" },
  { value: "Santiago del Estero", label: "Santiago del Estero" },
  { value: "Tierra del Fuego", label: "Tierra del Fuego" },
  { value: "Tucumán", label: "Tucumán" },
];

const ORIGIN_ROWS = [
  [
    { key: "street", label: "Calle", max: 255, grow: "3 1 0" },
    { key: "number", label: "Altura", max: 20, grow: "1 1 0" },
  ],
  [{ key: "unit", label: "Piso / Depto (opcional)", max: 50, grow: "1 1 0" }],
  [
    { key: "zip", label: "CP", max: 20, grow: "1 1 0" },
    { key: "city", label: "Localidad", max: 255, grow: "2 1 0" },
    {
      key: "province",
      label: "Provincia",
      max: 255,
      grow: "2 1 0",
      options: ARGENTINA_PROVINCES,
    },
  ],
];

/** `unit` es el unico opcional. Sin los otros cinco, ningun envio Flex puede crearse. */

const REQUIRED_ORIGIN_FIELDS = ["street", "number", "zip", "city", "province"];

const EMPTY_ORIGIN = () => ({
  street: null,
  number: null,
  unit: null,
  zip: null,
  city: null,
  province: null,
});

/** Solo para renderizar la pantalla si el endpoint no responde; nunca se envia solo. */
const DEFAULT_CONFIG = () => ({
  auto_shipment_enabled: false,
  origin_address: EMPTY_ORIGIN(),
  schedules: [
    { profile: "weekdays", enabled: false, start_time: null, end_time: null },
    { profile: "saturday", enabled: false, start_time: null, end_time: null },
    { profile: "sunday", enabled: false, start_time: null, end_time: null },
  ],
});

export default {
  name: "MeliFlexSettings",
  components: {
    Spinner,
  },
  data() {
    return {
      loading: true,
      saving: false,
      available: true,
      unavailableReason: "",
      config: DEFAULT_CONFIG(),
      ORIGIN_ROWS,
      // Claves: el perfil del horario (`weekdays`, ...) o `origin_address.<campo>`.
      errors: {},
      profileLabels: {
        weekdays: "Lunes a viernes",
        saturday: "Sábados",
        sunday: "Domingos",
      },
    };
  },
  computed: {
    originIncomplete() {
      const address = this.config.origin_address || {};
      return REQUIRED_ORIGIN_FIELDS.some((key) => !String(address[key] || "").trim());
    },
  },
  async mounted() {
    await this.loadConfig();
  },
  methods: {
    async loadConfig() {
      try {
        this.loading = true;
        const { data } = await getFlexConfig();
        this.config = this.normalize(data);
        this.available = true;
      } catch (error) {
        const status = error.response && error.response.status;
        this.available = false;
        this.unavailableReason = status ? `HTTP ${status}` : "sin respuesta";
        this.config = DEFAULT_CONFIG();
      } finally {
        this.loading = false;
      }
    },

    /** Si el backend devolviera menos de 3 perfiles, la pantalla no puede quedar sin filas. */
    normalize(data) {
      const base = DEFAULT_CONFIG();
      const received = (data && data.schedules) || [];

      return {
        auto_shipment_enabled: !!(data && data.auto_shipment_enabled),
        // Los seis campos vienen siempre, pero cada uno puede ser null.
        origin_address: { ...EMPTY_ORIGIN(), ...((data && data.origin_address) || {}) },
        schedules: base.schedules.map((fallback) => {
          const found = received.find((s) => s.profile === fallback.profile);
          return found ? { ...fallback, ...found } : fallback;
        }),
      };
    },

    originErrorKey(field) {
      return `origin_address.${field}`;
    },

    /** Se guarda "" y no null: el backend convierte los vacios a null y asi el campo no muestra el valor anterior. */
    /** Un `<select>` cuyo `value` no esta entre sus opciones se muestra vacio y al guardar borraria el dato. */
    originValueOutsideOptions(field) {
      const value = this.config.origin_address[field.key];
      if (!value) return false;
      return !field.options.some((opcion) => opcion.value === value);
    },

    setOriginField(field, value) {
      this.config.origin_address = { ...this.config.origin_address, [field]: value };
      this.errors = { ...this.errors, [this.originErrorKey(field)]: null };
    },

    toggleSchedule(index, enabled) {
      const schedule = this.config.schedules[index];
      schedule.enabled = enabled;
      // Deshabilitado va con horas en null, es lo que espera el PUT.
      if (!enabled) {
        schedule.start_time = null;
        schedule.end_time = null;
      }
      this.errors = { ...this.errors, [schedule.profile]: null };
    },

    setTime(index, field, value) {
      const schedule = this.config.schedules[index];
      schedule[field] = value || null;
      this.errors = { ...this.errors, [schedule.profile]: null };
    },

    isRequired(field) {
      return REQUIRED_ORIGIN_FIELDS.indexOf(field) !== -1;
    },

    /** Comparar "HH:MM" como string alcanza: el input type=time siempre devuelve dos digitos. */
    validate() {
      const errors = {};
      const address = this.config.origin_address || {};

      REQUIRED_ORIGIN_FIELDS.forEach((field) => {
        if (String(address[field] || "").trim()) return;
        errors[this.originErrorKey(field)] = field === "province"
          ? "Elegí una provincia."
          : "Este campo es obligatorio.";
      });

      this.config.schedules.forEach((schedule) => {
        if (!schedule.enabled) return;

        if (!schedule.start_time || !schedule.end_time) {
          errors[schedule.profile] = "Completá la hora de inicio y de fin.";
          return;
        }
        if (schedule.end_time <= schedule.start_time) {
          errors[schedule.profile] = "La hora de fin debe ser posterior a la de inicio.";
        }
      });

      this.errors = errors;
      return !Object.keys(errors).length;
    },

    /** Laravel devuelve `schedules.0.end_time`; el indice se traduce al perfil para mostrar el error en su fila. */
    applyServerErrors(error) {
      const data = error.response && error.response.data;
      const serverErrors = (data && data.errors) || {};
      const errors = {};

      Object.keys(serverErrors).forEach((key) => {
        const messages = serverErrors[key];
        const first = Array.isArray(messages) ? messages[0] : messages;

        // Hoy solo se valida el largo, pero la cobertura por codigo postal de CDS va a llegar por este camino.
        if (key.indexOf("origin_address.") === 0) {
          errors[key] = first;
          return;
        }

        const match = key.match(/^schedules\.(\d+)\./);
        if (!match) return;
        const schedule = this.config.schedules[Number(match[1])];
        if (!schedule) return;
        errors[schedule.profile] = first;
      });

      this.errors = errors;
      return Object.keys(errors).length > 0;
    },

    // El boton esta al final del formulario y la direccion arriba: sin esto el error puede
    // quedar fuera de pantalla y el guardado parece no hacer nada. `focus` ademas scrollea.
    focusFirstError() {
      const field = REQUIRED_ORIGIN_FIELDS.find(
        (key) => this.errors[this.originErrorKey(key)]
      );
      if (!field) return;
      const el = document.getElementById(`origin-${field}`);
      if (el && el.focus) el.focus();
    },

    async save() {
      if (!this.validate()) {
        this.focusFirstError();
        return;
      }

      try {
        this.saving = true;
        const { data } = await updateFlexConfig({
          auto_shipment_enabled: this.config.auto_shipment_enabled,
          origin_address: this.config.origin_address,
          schedules: this.config.schedules,
        });
        this.config = this.normalize(data);
        showSuccess({ message: "Configuración guardada." });
      } catch (error) {
        if (!this.applyServerErrors(error)) {
          showError({ message: errorMessage(error, "No se pudo guardar la configuración.") });
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
